import React, {useEffect, useState} from 'react';
import {RouteComponentProps} from 'react-router-dom';
import {css} from 'emotion';
import {DataType} from 'ka-table/enums';
import Button from '@amzn/meridian/button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import {getConstraint} from '../../../common/apis/BackendApisClient';
import {GetConstraintResponseSlotRow} from '../../../common/apis/models/getConstraintResponse';
import {getLocalizedAPIsStartDate, getLocalizedDateTimeStrings} from '../../../common/utils/dates';
import PageLoading from '../../../common/components/PageLoading';
import SiteDetailsTable from './SiteDetailsTable';
import {ConstraintValueRecord} from '../utils/analyzeConstraints';

const DAYS_PER_PAGE = 7;

interface IProps {
  history: RouteComponentProps['history'];
  windowConstraints: ConstraintValueRecord[];
  siteId: string;
  businessType: string;
  region: string;
  country?: string;
}

const navRowStyle = css`
  padding-bottom: 8px;
`;

const SiteWindowConstraintTable = ({history, windowConstraints, siteId, businessType, region, country}: IProps) => {
  const [slotData, setSlotData] = useState<ConstraintValueRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);

  const currDate = new Date();
  const baseStartTime = getLocalizedAPIsStartDate(currDate);

  const offsetDate = new Date(baseStartTime);
  offsetDate.setDate(offsetDate.getDate() + weekOffset * 7);
  const startTime = getLocalizedDateTimeStrings(offsetDate).date;

  useEffect(() => {
    const fetchAllWindowData = async () => {
      setLoading(true);
      const allRows: ConstraintValueRecord[] = [];

      for (const constraint of windowConstraints) {
        const response = await getConstraint({
          businessType,
          region,
          country,
          constraintId: Number(constraint.constraintId),
          startTime: baseStartTime,
          query: siteId,
        });

        if (response && response.slotData && response.slotData.length > 0) {
          const siteSlotData = response.slotData.find((s) => s.siteId === siteId);
          if (siteSlotData) {
            const rows = transformSlotDataToRows(constraint, siteSlotData, startTime);
            allRows.push(...rows);
          }
        }
      }

      setSlotData(allRows);
      setLoading(false);
    };

    fetchAllWindowData();
  }, [windowConstraints, siteId, businessType, region, country, baseStartTime, startTime]);

  // Date range label
  const rangeStart = new Date(startTime);
  const rangeEnd = new Date(startTime);
  rangeEnd.setDate(rangeEnd.getDate() + DAYS_PER_PAGE - 1);
  const rangeLabel = `${rangeStart.toLocaleString('default', {month: 'short', day: '2-digit'})} - ${rangeEnd.toLocaleString('default', {month: 'short', day: '2-digit'})}`;

  if (loading) {
    return <PageLoading />;
  }

  if (slotData.length === 0) {
    return null;
  }

  return (
    <div>
      <Row className={navRowStyle} spacing="small" alignmentVertical="center">
        <Button type="tertiary" size="small" onClick={() => setWeekOffset(Math.max(0, weekOffset - 1))} disabled={weekOffset === 0}>
          ← Prev
        </Button>
        <Text type="b200">{rangeLabel}</Text>
        <Button type="tertiary" size="small" onClick={() => setWeekOffset(weekOffset + 1)} disabled={weekOffset >= 12}>
          Next →
        </Button>
      </Row>
      <SiteDetailsTable
        key={weekOffset}
        history={history}
        allowSorting={false}
        allowPaging={false}
        allowFiltering={false}
        constraintData={slotData}
        highlightedDates={{}}
        summaryRecords={[]}
        extraStaticColumns={[{
          key: 'slotNumber',
          title: 'Slot',
          dataType: DataType.String,
          style: {width: 60},
        }]}
        extraStaticColumnNames={['slotNumber', 'siteGroupId', 'expandId']}
        treeGroupKeyField="siteGroupId"
      />
    </div>
  );
};

function transformSlotDataToRows(
  constraint: ConstraintValueRecord,
  siteSlotData: GetConstraintResponseSlotRow,
  startTime: string
): ConstraintValueRecord[] {
  const rows: ConstraintValueRecord[] = [];
  const startDate = new Date(startTime);
  const parentId = `${constraint.constraintId}-total`;

  const days: string[] = [];
  for (let i = 0; i < DAYS_PER_PAGE; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    days.push(getLocalizedDateTimeStrings(d).date);
  }

  // Parent row (All Slots total) - siteGroupId = null means top-level
  const totalRow: ConstraintValueRecord = {
    constraintId: parentId,
    constraintName: constraint.constraintName,
    constraintValueType: constraint.constraintValueType as string,
    slotNumber: 'All Slots',
    siteGroupId: null,
    constraintUsageTags: [],
    constraint: {constraintName: constraint.constraintName, constraintId: constraint.constraintId},
  };

  days.forEach((day) => {
    let sum = 0;
    let hasValue = false;
    for (let slot = siteSlotData.firstSlot; slot <= siteSlotData.lastSlot; slot++) {
      const val = getSlotValue(siteSlotData, day, slot);
      if (val) {
        sum += parseFloat(val.replace(/,/g, ''));
        hasValue = true;
      }
    }
    totalRow[day] = hasValue ? sum.toLocaleString(undefined, {maximumFractionDigits: 2}) : '';
  });

  rows.push(totalRow);

  // Child rows (individual slots) - siteGroupId = parentId means child of total
  for (let slot = siteSlotData.firstSlot; slot <= siteSlotData.lastSlot; slot++) {
    const slotRow: ConstraintValueRecord = {
      constraintId: `${constraint.constraintId}-slot-${slot}`,
      constraintName: '',
      constraintValueType: '',
      slotNumber: String(slot),
      siteGroupId: parentId,
      constraintUsageTags: [],
      constraint: {constraintName: '', constraintId: ''},
    };

    days.forEach((day) => {
      const val = getSlotValue(siteSlotData, day, slot);
      slotRow[day] = val || '';
    });

    rows.push(slotRow);
  }

  return rows;
}

function getSlotValue(siteSlotData: GetConstraintResponseSlotRow, day: string, slot: number): string | undefined {
  const dayDate = new Date(day);
  const weekStart = new Date(dayDate);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekKey = getLocalizedDateTimeStrings(weekStart).date;

  const weekData = siteSlotData.weekData[weekKey];
  if (!weekData) return undefined;

  const dayData = weekData[day];
  if (!dayData) return undefined;

  return dayData[`${slot}`];
}

export default SiteWindowConstraintTable;
